import { Link } from 'react-router-dom';
import { formatCents, formatCount, formatPercent, formatEpc } from '@affily/shared/money';
import { describeTerms } from '@affily/shared/commission';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { MetricRow, performanceMetrics, MoneyByCurrency } from '../../components/Metrics.jsx';
import { TransparencyBadge } from '../../components/Transparency.jsx';
import { OnboardingChecklist } from '../../components/OnboardingChecklist.jsx';
import {
  Button,
  Badge,
  Spinner,
  EmptyState,
  Unknown,
  SectionTitle,
  Alert,
} from '../../components/ui.jsx';

export default function BrandOverview() {
  const { data, loading } = useFetch('/dashboard/brand');

  if (loading) {
    return (
      <DashboardLayout side="brand">
        <Spinner />
      </DashboardLayout>
    );
  }

  if (!data?.profile) {
    return (
      <DashboardLayout side="brand" title="Overview">
        <OnboardingChecklist side="brand" className="mb-6" />
        <EmptyState
          title="Set up your brand profile"
          action={<Button to="/dashboard/brand/profile">Get started</Button>}
        >
          One short form, then you can list a program.
        </EmptyState>
      </DashboardLayout>
    );
  }

  const { profile, totals, commissions, programs, topCreators, unattributedConversions } = data;

  return (
    <DashboardLayout
      side="brand"
      title="Overview"
      description="Spend, performance and what you owe. Including the parts that look bad."
      actions={
        // A draft's page is a 404, so there is nothing to view yet.
        profile.isPublished && (
          <Button to={`/brands/${profile.slug}`} variant="outline" size="sm">
            View public page
          </Button>
        )
      }
    >
      <div className="space-y-8">
        <OnboardingChecklist side="brand" />
        {unattributedConversions > 0 && (
          <Alert tone="caution">
            {unattributedConversions} conversion{unattributedConversions === 1 ? '' : 's'} couldn’t
            be attributed to any click. That usually means the <code>aff_click</code> parameter
            isn’t reaching your checkout — worth fixing, since those sales earned nobody a
            commission. We show them rather than hiding them: a broken integration otherwise reads
            as weak demand.
          </Alert>
        )}

        <section>
          <SectionTitle hint="Across all programs, all time">Performance</SectionTitle>
          <MetricRow metrics={performanceMetrics(totals, data.money)} />
        </section>

        <section>
          <SectionTitle
            hint="What you owe creators"
            action={
              <Button to="/dashboard/brand/settle" size="sm">
                Settle &amp; pay
              </Button>
            }
          >
            Commissions
          </SectionTitle>
          <MoneyByCurrency commissions={commissions} money={data.money} side="brand" />
        </section>

        <section>
          <SectionTitle
            action={
              <Button to="/dashboard/brand/programs" variant="ghost" size="sm">
                Manage programs
              </Button>
            }
          >
            Program performance
          </SectionTitle>

          {programs.length === 0 ? (
            <EmptyState
              title="No programs yet"
              action={<Button to="/dashboard/brand/programs/new">List a program</Button>}
            />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Program</th>
                    <th>Terms</th>
                    <th className="num">Partners</th>
                    <th className="num">Clicks</th>
                    <th className="num">Conv.</th>
                    <th className="num">CVR</th>
                    <th className="num">EPC</th>
                    <th className="num">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {programs.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <Link
                          to={`/dashboard/brand/programs/${p.id}`}
                          className="font-medium text-ink hover:text-accent"
                        >
                          {p.title}
                        </Link>
                        <Badge className="ml-2">{p.status}</Badge>
                      </td>
                      <td className="tabular-nums">{describeTerms(p)}</td>
                      <td className="num">{formatCount(p.stats.activePartnerships)}</td>
                      <td className="num">{formatCount(p.stats.uniqueClicks)}</td>
                      <td className="num">{formatCount(p.stats.conversions)}</td>
                      <td className="num">
                        {p.stats.conversionRatePct === null ? (
                          <Unknown reason="No clicks yet" />
                        ) : (
                          formatPercent(p.stats.conversionRatePct)
                        )}
                      </td>
                      <td className="num">
                        {p.stats.epc === null ? (
                          <Unknown reason="No clicks yet" />
                        ) : (
                          formatEpc(p.stats.epc, { currency: p.currency })
                        )}
                      </td>
                      <td className="num font-medium">
                        {formatCents(p.stats.commissionGrossCents, { currency: p.currency })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <SectionTitle hint="Ranked by commission earned — your actual performers">
            Top creators
          </SectionTitle>
          {topCreators.length === 0 ? (
            <EmptyState
              title="No partnerships yet"
              action={<Button to="/creators">Find creators</Button>}
            />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Creator</th>
                    <th>Program</th>
                    <th className="num">Clicks</th>
                    <th className="num">Conv.</th>
                    <th className="num">CVR</th>
                    <th className="num">Revenue</th>
                    <th className="num">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {topCreators.map((row) => (
                    <tr key={row.partnershipId}>
                      <td>
                        {row.creator ? (
                          <>
                            <Link
                              to={`/creators/${row.creator.slug}`}
                              className="font-medium text-ink hover:text-accent"
                            >
                              {row.creator.displayName}
                            </Link>
                            <TransparencyBadge
                              transparency={{
                                score: row.creator.transparencyScore,
                                tier: 'high',
                                tierLabel: '',
                              }}
                              size="sm"
                              showDetail={false}
                            />
                          </>
                        ) : (
                          <span className="text-ink-muted">Creator removed</span>
                        )}
                      </td>
                      <td className="text-sm text-ink-muted">{row.program?.title ?? '—'}</td>
                      <td className="num">{formatCount(row.stats.uniqueClicks)}</td>
                      <td className="num">{formatCount(row.stats.conversions)}</td>
                      <td className="num">
                        {row.stats.conversionRatePct === null ? (
                          <Unknown reason="No clicks yet" />
                        ) : (
                          formatPercent(row.stats.conversionRatePct)
                        )}
                      </td>
                      <td className="num">{formatCents(row.stats.revenueCents, { currency: row.currency })}</td>
                      <td className="num font-medium">
                        {formatCents(row.stats.commissionGrossCents, { currency: row.currency })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
